import React, { useCallback, useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import { message, Image } from 'antd';
import ENVIRONMENT from '../../../../environments';
import CustomerMainLayout from '../CustomerMainLayout';
import SubscriptionUpgradeManager from '../SubscriptionUpgradeManager/SubscriptionUpgradeManager';
import EditPropModal from './EditPropModal';
import DeleteEntityModal from '../../DeleteEntityModal/DeleteEntityModal';
import CreateProductModal from '../../AiAsserCreate/CreateProductModal';

const getIframeUserContextPayload = () => {
  const keys = [
    'username',
    'company_id',
    'shared_entities',
    'all3d_jwt_token',
    'user_role',
    'is_admin',
    'is_msprovider',
    'managed_company_id',
    'super_admin_username',
    'is_ai_customer',
  ];

  return keys.reduce((acc, key) => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const MigratedPropsLibrary = ({ location, match }) => {
  const iframeRef = React.useRef(null);
  const migratedPortalBaseUrl = (ENVIRONMENT.MIGRATED_PORTAL_URL || process.env.REACT_APP_MIGRATED_PORTAL_URL || 'http://localhost:5173').replace(/\/$/, '');
  const propId = match?.params?.id;
  const iframeSrc = propId
    ? `${migratedPortalBaseUrl}/props/${propId}`
    : `${migratedPortalBaseUrl}/props-library${location?.search || ''}`;

  const targetOrigin = useMemo(() => {
    try {
      return new URL(migratedPortalBaseUrl).origin;
    } catch (error) {
      return '*';
    }
  }, [migratedPortalBaseUrl]);

  // Subscription state
  const [subscriptionUpgradeManagerModel, setSubscriptionUpgradeManagerModel] = useState(false);
  const [currentRequestType, setCurrentRequestType] = useState('ai_model');
  const [cardStatus, setCardStatus] = useState(0);

  // Edit modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);

  // Delete modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);

  // Add prop modal state
  const [createPropModalVisible, setCreatePropModalVisible] = useState(false);

  // Image preview state
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);

  const sendToIframe = useCallback((msg) => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (iframeWindow) {
      iframeWindow.postMessage(msg, targetOrigin);
    }
  }, [targetOrigin]);

  const loadUserRequestStatus = useCallback(() => {
    const payload = {
      username: localStorage.getItem('username'),
      request_type: currentRequestType,
      action: 'check',
      category: '',
    };
    axios.post(ENVIRONMENT.PAYMENT_GATEWAY, payload).then((res) => {
      setCardStatus(res.data['card_status']);
      sendToIframe({ type: 'ALL3D_REFRESH_USER_REQUEST_STATUS' });
    });
  }, [currentRequestType, sendToIframe]);

  const postUserContext = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;

    iframeWindow.postMessage(
      {
        type: 'ALL3D_USER_CONTEXT',
        payload: getIframeUserContextPayload(),
      },
      targetOrigin
    );
  }, [targetOrigin]);

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleteLoader(true);
    try {
      const payload = {
        action: 'delete_prop',
        prop_id: itemToDelete.id,
        username: localStorage.getItem('username'),
        company_id: localStorage.getItem('company_id'),
      };
      await axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload);
      sendToIframe({ type: 'PROP_ITEM_DELETED', payload: { id: itemToDelete.id } });
      setDeleteModalVisible(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting prop:', error);
    } finally {
      setDeleteLoader(false);
    }
  };

  const handleEditSuccess = (updatedItem) => {
    sendToIframe({ type: 'PROP_ITEM_UPDATED', payload: updatedItem });
  };

  const handleCreatePropModalOk = async (data) => {
    const { files, dimensions, name, category, source } = data;
    if (source === 'library') {
      setCreatePropModalVisible(false);
      return;
    }
    if (!files || files.length === 0) {
      message.error('Please upload a prop image');
      return;
    }
    const file = files[0];
    const imageUrl = ENVIRONMENT.getBaseURL() + encodeURIComponent('test_files/' + file.uid + '/' + file.name);
    const payload = {
      action: 'add_prop',
      image_url: imageUrl,
      height: dimensions?.height || 0,
      width: dimensions?.width || 0,
      depth: dimensions?.depth || 0,
      username: localStorage.getItem('username'),
      name: name || 'Untitled',
      category: category || '',
      company_id: localStorage.getItem('company_id'),
    };
    setCreatePropModalVisible(false);
    try {
      const response = await axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload);
      if (response.data && response.data.result === 'success') {
        message.success('Prop added successfully');
        sendToIframe({ type: 'PROP_REFRESH' });
      } else {
        message.error('Failed to add prop');
      }
    } catch (error) {
      console.error('Error adding prop:', error);
      message.error('Error adding prop');
    }
  };

  const handleBulkPropUpload = async (data) => {
    const { csvFile, requestName } = data;
    const file_uri = 'test_files/' + csvFile.uid + '/' + csvFile.name;
    const payload = {
      action: 'submit_bulk_props',
      csv_file_uri: file_uri,
      bulk_upload_name: requestName,
      company_id: localStorage.getItem('company_id'),
      username: localStorage.getItem('username'),
    };
    try {
      await axios.post(ENVIRONMENT.AI_BULK_JOB_CONTROLLER, payload);
      sendToIframe({ type: 'PROP_REFRESH' });
    } catch (error) {
      console.error('Error bulk uploading props:', error);
      message.error('Error uploading props');
    }
  };

  useEffect(() => {
    const onMessage = (event) => {
      if (targetOrigin !== '*' && event.origin !== targetOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const { type, payload } = event?.data || {};

      if (type === 'ALL3D_USER_CONTEXT_REQUEST') {
        postUserContext();
      } else if (type === 'ALL3D_OUT_OF_CREDITS') {
        const { requestType } = payload || {};
        if (requestType) setCurrentRequestType(requestType);
        loadUserRequestStatus();
        setSubscriptionUpgradeManagerModel(true);
      } else if (type === 'ALL3D_NAVIGATE') {
        window.location.href = event.data.url || '';
      } else if (type === 'PROP_OPEN_ADD_MODAL') {
        setCreatePropModalVisible(true);
      } else if (type === 'PROP_OPEN_EDIT_MODAL') {
        setItemToEdit(payload);
        setEditModalVisible(true);
      } else if (type === 'PROP_OPEN_DELETE_MODAL') {
        setItemToDelete(payload);
        setDeleteModalVisible(true);
      } else if (type === 'PROP_OPEN_PREVIEW') {
        setPreviewSrc(payload?.src || '');
        setPreviewVisible(true);
      } else if (type === 'PROP_NAVIGATE_DETAIL') {
        // Update parent URL to reflect prop detail view without full reload
        const propId = payload?.id;
        if (propId) {
          window.history.pushState({}, '', `/props-library/${propId}`);
        }
      } else if (type === 'PROP_NAVIGATE_BACK') {
        // Navigate parent back to props library list
        window.history.pushState({}, '', '/props-library');
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [postUserContext, targetOrigin, loadUserRequestStatus]);

  return (
    <CustomerMainLayout selected="34" remove_global_padding={true}>
      <div style={{ width: '100%', height: '100%' }}>
        <iframe
          ref={iframeRef}
          title="Props Library"
          src={iframeSrc}
          onLoad={postUserContext}
          style={{
            width: '100%',
            height: '100%',
            border: '0',
            display: 'block',
          }}
        />
      </div>

      {/* <Image
        width={0}
        style={{ display: 'none' }}
        src={previewSrc}
        preview={{
          visible: previewVisible,
          src: previewSrc,
          onVisibleChange: (val) => {
            setPreviewVisible(val);
            if (!val) setPreviewSrc('');
          },
        }}
      /> */}

      <CreateProductModal
        visible={createPropModalVisible}
        onOk={handleCreatePropModalOk}
        onCancel={() => setCreatePropModalVisible(false)}
        mode="prop"
        propsLibrary={[]}
        showNameField={true}
        showCategoryField={true}
        showBulkUploadTab={true}
        bulkUploadType="prop"
        onBulkUploadSubmit={handleBulkPropUpload}
      />

      <EditPropModal
        visible={editModalVisible}
        onCancel={() => { setEditModalVisible(false); setItemToEdit(null); }}
        onSuccess={handleEditSuccess}
        itemToEdit={itemToEdit}
      />

      <DeleteEntityModal
        visible={deleteModalVisible}
        onCancel={() => { setDeleteModalVisible(false); setItemToDelete(null); }}
        heading="Delete Prop"
        subText="Are you sure you want to delete this prop? This action cannot be undone."
        delete_model={handleConfirmDelete}
        selected_id={itemToDelete?.id}
        deletingModelLoader={deleteLoader}
      />

      <SubscriptionUpgradeManager
        subscriptionUpgradeManagerModel={subscriptionUpgradeManagerModel}
        setSubscriptionUpgradeManagerModel={setSubscriptionUpgradeManagerModel}
        cardStatus={cardStatus}
        loadUserRequestStatus={loadUserRequestStatus}
        requestType={currentRequestType}
      />
    </CustomerMainLayout>
  );
};

export default MigratedPropsLibrary;
